import type { Cell } from '../types';
import { countYugos, isValidMove, copyBoard, checkForYugos, checkForIgo, makeMove, isEmptyCell } from '../logic';
import { processYugos } from '../logic/yugos';
import { wouldCreateLineTooLong } from '../logic/board';

// Advanced position evaluation

const evaluatePosition = (board: (Cell | null)[][], playerColor: 'white' | 'black'): number => {
    const opponentColor = playerColor === 'white' ? 'black' : 'white';
    let score = 0;
    
    // 1. Material evaluation (yugos and their point values)
    const playerYugos = countYugos(board, playerColor);
    const opponentYugos = countYugos(board, opponentColor);
    score += (playerYugos - opponentYugos) * 100;
    
    // 2. Immediate threats (igo and yugo formations)
    score += evaluateThreats(board, playerColor);
    
    // 3. Positional factors
    score += evaluatePositional(board, playerColor);
    
    // 4. Strategic factors
    score += evaluateStrategic(board, playerColor);
    
    // 5. Tactical patterns
    score += evaluateTactical(board, playerColor);
    
    // 6. yugo building: reward longer yugos of own color
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const cell = board[row][col];
        if (cell && cell.color === playerColor) {
          // Check in all 4 directions for yugos
          const directions = [[1,0],[0,1],[1,1],[1,-1]];
          for (const [dr,dc] of directions) {
            let length = 1;
            let r = row + dr, c = col + dc;
            while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c]?.color === playerColor) {
              length++;
              r += dr; c += dc;
            }
            if (length >= 2) score += length * 12; // reward longer yugo
          }
        }
      }
    }
    
    return score;
  };
  
  const evaluateThreats = (board: (Cell | null)[][], playerColor: 'white' | 'black'): number => {
    let score = 0;
    const opponentColor = playerColor === 'white' ? 'black' : 'white';
    
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (!isValidMove(board, row, col, playerColor)) continue;
        
        // Test move for player
        const testBoard = copyBoard(board);
        testBoard[row][col] = { color: playerColor, isYugo: false };
        
        // Check for yugos
        const yugos = checkForYugos(testBoard, row, col, playerColor);
        if (yugos.length > 0) {
          score += 1000 * yugos.length; // Yugo formation bonus
        }
        
        // Check for igo (with yugo)
        testBoard[row][col] = { color: playerColor, isYugo: true };
        const igo = checkForIgo(testBoard, row, col, playerColor);
        if (igo) {
          score += 10000; // Instant win
        }
        
        // Check blocking opponent threats
        const opponentTestBoard = copyBoard(board);
        opponentTestBoard[row][col] = { color: opponentColor, isYugo: false };
        const opponentYugos = checkForYugos(opponentTestBoard, row, col, opponentColor);
        if (opponentYugos.length > 0) {
          score += 800 * opponentYugos.length; // Block opponent yugos
        }
        
        opponentTestBoard[row][col] = { color: opponentColor, isYugo: true };
        const opponentIgo = checkForIgo(opponentTestBoard, row, col, opponentColor);
        if (opponentIgo) {
          score += 9000; // Block opponent igo
        }
      }
    }
    
    return score;
  };
  
  const evaluatePositional = (board: (Cell | null)[][], playerColor: 'white' | 'black'): number => {
    let score = 0;
    const opponentColor = playerColor === 'white' ? 'black' : 'white';
    
    // Center control evaluation
    const centerSquares = [[3,3], [3,4], [4,3], [4,4]];
    for (const [row, col] of centerSquares) {
      const cell = board[row][col];
      if (cell?.color === playerColor) {
        score += 50;
      } else if (cell?.color === opponentColor) {
        score -= 50;
      }
    }
    
    // Extended center control
    const extendedCenter = [[2,2], [2,3], [2,4], [2,5], [3,2], [3,5], [4,2], [4,5], [5,2], [5,3], [5,4], [5,5]];
    for (const [row, col] of extendedCenter) {
      const cell = board[row][col];
      if (cell?.color === playerColor) {
        score += 20;
      } else if (cell?.color === opponentColor) {
        score -= 20;
      }
    }
    
    // Piece activity and mobility
    score += evaluateMobility(board, playerColor) - evaluateMobility(board, opponentColor);
    
    return score;
  };
  
  const evaluateMobility = (board: (Cell | null)[][], playerColor: 'white' | 'black'): number => {
    let mobility = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (isValidMove(board, row, col, playerColor)) {
          mobility++;
        }
      }
    }
    return mobility * 5; // Each possible move is worth 5 points
  };
  
  const evaluateStrategic = (board: (Cell | null)[][], playerColor: 'white' | 'black'): number => {
    let score = 0;
    
    // Connectivity bonus - pieces supporting each other
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const cell = board[row][col];
        if (cell?.color === playerColor) {
          let connections = 0;
          const directions = [[-1,-1], [-1,0], [-1,1], [0,-1], [0,1], [1,-1], [1,0], [1,1]];
          
          for (const [dr, dc] of directions) {
            const newRow = row + dr;
            const newCol = col + dc;
            if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
              const adjCell = board[newRow][newCol];
              if (adjCell?.color === playerColor) {
                connections++;
              }
            }
          }
          
          score += connections * 15; // Connectivity bonus
          
          // Yugo protection bonus
          if (cell.isYugo) {
            score += connections * 25; // Extra bonus for protecting yugos
          }
        }
      }
    }
    
    return score;
  };
  
  const evaluateTactical = (board: (Cell | null)[][], playerColor: 'white' | 'black'): number => {
    let score = 0;
    
    // Look for fork opportunities (moves that create multiple threats)
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (!isValidMove(board, row, col, playerColor)) continue;
        
        const testBoard = copyBoard(board);
        testBoard[row][col] = { color: playerColor, isYugo: false };
        
        let threats = 0;
        
        // Count potential yugos from this position
        const yugos = checkForYugos(testBoard, row, col, playerColor);
        threats += yugos.length;
        
        // Check if this move creates multiple line possibilities
        const directions = [[-1,-1], [-1,0], [-1,1], [0,-1], [0,1], [1,-1], [1,0], [1,1]];
        for (const [dr, dc] of directions) {
          let lineLength = 1;
          
          // Count in positive direction
          let r = row + dr, c = col + dc;
          while (r >= 0 && r < 8 && c >= 0 && c < 8 && testBoard[r][c]?.color === playerColor) {
            lineLength++;
            r += dr;
            c += dc;
          }
          
          // Count in negative direction
          r = row - dr;
          c = col - dc;
          while (r >= 0 && r < 8 && c >= 0 && c < 8 && testBoard[r][c]?.color === playerColor) {
            lineLength++;
            r -= dr;
            c -= dc;
          }
          
          if (lineLength >= 3) {
            threats++;
          }
        }
        
        if (threats >= 2) {
          score += 200 * threats; // Fork bonus
        }
      }
    }
    
    return score;
  };

// Threat detection functions
export const detectThreeYugoThreat = (board: (Cell | null)[][], opponentColor: 'white' | 'black'): {row: number, col: number}[] => {
  const threats: {row: number, col: number}[] = [];
  const directions = [[-1, 0], [0, 1], [1, 1], [1, 0]]; // All 4 main directions
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      for (const [dr, dc] of directions) {
        // Check for pattern: Yugo-Yugo-Empty-Yugo or Yugo-Empty-Yugo-Yugo or Empty-Yugo-Yugo-Yugo
        const positions = [];
        for (let i = 0; i < 4; i++) {
          const r = row + i * dr;
          const c = col + i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            positions.push({row: r, col: c, cell: board[r][c]});
          }
        }
        
        if (positions.length === 4) {
          const yugos = positions.filter(p => p.cell?.color === opponentColor && p.cell?.isYugo);
          const empties = positions.filter(p => isEmptyCell(board, p.row, p.col));
          
          // Check if we have exactly 3 yugos and 1 empty in the line
          if (yugos.length === 3 && empties.length === 1) {
            threats.push({row: empties[0].row, col: empties[0].col});
          }
        }
      }
    }
  }
  
  return threats;
};

export const detectIgoFork = (board: (Cell | null)[][], opponentColor: 'white' | 'black'): {row: number, col: number}[] => {
  const forkThreats: {row: number, col: number}[] = [];
  const directions = [[-1, 0], [0, 1], [1, 1], [1, 0]];
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (!isEmptyCell(board, row, col)) continue;
      
      for (const [dr, dc] of directions) {
        // Check for pattern: Yugo-Empty-Yugo-Empty-[THIS CELL]-Empty-Yugo
        // This would create two threats if opponent places yugo here
        
        let yugosOnLeft = 0;
        let yugosOnRight = 0;
        
        // Check left side (2 positions)
        for (let i = 1; i <= 2; i++) {
          const r = row - i * dr;
          const c = col - i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            const cell = board[r][c];
            if (cell?.color === opponentColor && cell?.isYugo) {
              yugosOnLeft++;
            }
          }
        }
        
        // Check right side (2 positions)
        for (let i = 1; i <= 2; i++) {
          const r = row + i * dr;
          const c = col + i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            const cell = board[r][c];
            if (cell?.color === opponentColor && cell?.isYugo) {
              yugosOnRight++;
            }
          }
        }
        
        // If placing a yugo here would connect with yugos on both sides
        // and create 3+ connected yugos, it's a fork threat
        if (yugosOnLeft >= 1 && yugosOnRight >= 1 && (yugosOnLeft + yugosOnRight >= 2)) {
          forkThreats.push({row, col});
        }
      }
    }
  }
  
  return forkThreats;
};

export const detectYugoToForkThreat = (board: (Cell | null)[][], opponentColor: 'white' | 'black'): {row: number, col: number}[] => {
  const yugoToForkThreats: {row: number, col: number}[] = [];
  const directions = [[-1, 0], [0, 1], [1, 1], [1, 0]];
  
  // For each empty cell, check if opponent placing there creates a yugo that leads to fork
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (!isEmptyCell(board, row, col)) continue;
      
      // Simulate opponent placing a piece here
      const testBoard = copyBoard(board);
      testBoard[row][col] = { color: opponentColor, isYugo: false };
      
      // Check if this creates any yugos
      const yugos = checkForYugos(testBoard, row, col, opponentColor);
      
      if (yugos.length > 0) {
        // Simulate the yugo formation (piece becomes yugo, others removed)
        const postYugoBoard = copyBoard(testBoard);
        
        // Process each yugo
        for (const yugo of yugos) {
          // The placed piece becomes a yugo
          postYugoBoard[row][col] = { color: opponentColor, isYugo: true };
          
          // Remove other pieces in the yugo (except the new yugo)
          for (const pos of yugo) {
            if (pos.row !== row || pos.col !== col) {
              postYugoBoard[pos.row][pos.col] = null;
            }
          }
        }
        
        // Now check if this results in an igo fork situation
        // Look for 2+ connected yugos with empty cells on both ends
        for (const [dr, dc] of directions) {
          const line = [];
          
          // Build line in this direction starting from the new yugo
          for (let i = -3; i <= 3; i++) {
            const r = row + i * dr;
            const c = col + i * dc;
            if (r >= 0 && r < 8 && c >= 0 && c < 8) {
              line.push({
                row: r, 
                col: c, 
                cell: postYugoBoard[r][c],
                isEmpty: isEmptyCell(postYugoBoard, r, c)
              });
            }
          }
          
          // Look for patterns like: Empty-Yugo-Yugo-Empty or Empty-Yugo-Yugo-Yugo-Empty
          for (let start = 0; start < line.length - 3; start++) {
            const segment = line.slice(start, start + 4);
            const yugos = segment.filter(p => p.cell?.color === opponentColor && p.cell?.isYugo);
            
            // If we have 2+ yugos with empties on both ends, it's a fork threat
            if (yugos.length >= 2 && segment[0].isEmpty && segment[segment.length - 1].isEmpty) {
              yugoToForkThreats.push({row, col});
              break;
            }
          }
        }
      }
    }
  }
  
  return yugoToForkThreats;
};

export const detectYugoTrap = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): boolean => {
  const opponentColor = playerColor === 'white' ? 'black' : 'white';
  
  // Simulate placing the piece and forming yugos
  const testBoard = copyBoard(board);
  testBoard[row][col] = { color: playerColor, isYugo: false };
  
  const yugos = checkForYugos(testBoard, row, col, playerColor);
  
  if (yugos.length === 0) return false; // No yugo formed, no trap
  
  // For each yugo, check what ions would be removed
  for (const yugo of yugos) {
    for (const pos of yugo) {
      if (pos.row === row && pos.col === col) continue; // Skip the new piece
      
      // This migo would be removed - check if it was defending anything critical
      const migoRow = pos.row;
      const migoCol = pos.col;
      
      // Temporarily remove this migo and check for new threats
      const boardWithoutMigo = copyBoard(testBoard);
      boardWithoutMigo[migoRow][migoCol] = null;
      
      // Check if removing this migo exposes us to Three Yugo Threat
      const threeYugoThreats = detectThreeYugoThreat(boardWithoutMigo, opponentColor);
      if (threeYugoThreats.some(threat => threat.row === migoRow && threat.col === migoCol)) {
        return true; // This is a trap!
      }
      
      // Check if removing this migo exposes us to Igo Fork
      const igoForksExposed = detectIgoFork(boardWithoutMigo, opponentColor);
      if (igoForksExposed.some(fork => fork.row === migoRow && fork.col === migoCol)) {
        return true; // This is a trap!
      }
    }
  }
  
  return false;
};

// ============================================================================
// MUST-MOVE PATTERN DETECTION FUNCTIONS FOR AI-3
// ============================================================================

/**
 * Pattern #1: Must-block - Opponent has 3 Yugos with exactly 1 empty space
 * Returns the empty space that must be blocked (or null if no threat)
 */
export const detectMustBlockThreeYugo = (board: (Cell | null)[][], opponentColor: 'white' | 'black'): {row: number, col: number}[] => {
  // Check all 8 directions: horizontal, vertical, and both diagonals
  const directions = [
    [-1, 0],   // up (vertical)
    [0, 1],    // right (horizontal)
    [1, 1],    // down-right (diagonal)
    [1, 0],    // down (vertical)
    [0, -1],   // left (horizontal)
    [-1, 1],   // up-right (diagonal)
    [-1, -1],  // up-left (diagonal)
    [1, -1]    // down-left (diagonal)
  ];
  
  const threats: {row: number, col: number}[] = [];
  const threatSet = new Set<string>(); // Use Set to avoid duplicates
  
  // Check all possible lines in all directions
  for (let startRow = 0; startRow < 8; startRow++) {
    for (let startCol = 0; startCol < 8; startCol++) {
      for (const [dr, dc] of directions) {
        // Build the full line starting from this position
        const line: {row: number, col: number, cell: Cell | null}[] = [];
        for (let i = 0; i < 8; i++) {
          const r = startRow + i * dr;
          const c = startCol + i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            line.push({row: r, col: c, cell: board[r][c]});
          } else {
            break;
          }
        }
        
        // Check all 4-position segments in this line
        for (let start = 0; start <= line.length - 4; start++) {
          const segment = line.slice(start, start + 4);
          const yugos = segment.filter(p => p.cell?.color === opponentColor && p.cell?.isYugo);
          const opponentPieces = segment.filter(p => p.cell?.color === opponentColor);
          const empties = segment.filter(p => !p.cell || isEmptyCell(board, p.row, p.col));
          const otherPieces = segment.filter(p => p.cell && p.cell.color !== opponentColor);
          
          // Debug logging for segments with 3+ opponent pieces
          if (opponentPieces.length >= 3) {
            console.log(`[Must-Block Debug] Segment at (${segment[0].row},${segment[0].col}) to (${segment[3].row},${segment[3].col}): ${opponentPieces.length} opponent pieces (${yugos.length} Yugos), ${empties.length} empties, ${otherPieces.length} other pieces`);
          }
          
          // Must have exactly 3 yugos and exactly 1 empty (not 2 empties - that's unstoppable)
          if (yugos.length === 3 && empties.length === 1) {
            const emptyPos = empties[0];
            const posKey = `${emptyPos.row},${emptyPos.col}`;
            if (!threatSet.has(posKey)) {
              console.log(`[Must-Block Detection] Found 3 ${opponentColor} Yugos with 1 empty at (${emptyPos.row}, ${emptyPos.col})`);
              threats.push({row: emptyPos.row, col: emptyPos.col});
              threatSet.add(posKey);
            }
          }
        }
      }
    }
  }
  
  return threats;
};


/**
 * Pattern #2: Must-win - You have 3 Yugos with exactly 1 empty space
 * Returns the empty space that wins the game (or null if no win)
 */
export const detectMustWinThreeYugo = (board: (Cell | null)[][], playerColor: 'white' | 'black'): {row: number, col: number} | null => {
  // Check all 8 directions: horizontal, vertical, and both diagonals
  const directions = [
    [-1, 0],   // up (vertical)
    [0, 1],    // right (horizontal)
    [1, 1],    // down-right (diagonal)
    [1, 0],    // down (vertical)
    [0, -1],   // left (horizontal)
    [-1, 1],   // up-right (diagonal)
    [-1, -1],  // up-left (diagonal)
    [1, -1]    // down-left (diagonal)
  ];
  
  // Check all possible lines in all directions
  for (let startRow = 0; startRow < 8; startRow++) {
    for (let startCol = 0; startCol < 8; startCol++) {
      for (const [dr, dc] of directions) {
        // Build the full line starting from this position
        const line: {row: number, col: number, cell: Cell | null}[] = [];
        for (let i = 0; i < 8; i++) {
          const r = startRow + i * dr;
          const c = startCol + i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            line.push({row: r, col: c, cell: board[r][c]});
          } else {
            break;
          }
        }
        
        // Check all 4-position segments in this line
        for (let start = 0; start <= line.length - 4; start++) {
          const segment = line.slice(start, start + 4);
          const yugos = segment.filter(p => p.cell?.color === playerColor && p.cell?.isYugo);
          const empties = segment.filter(p => !p.cell || isEmptyCell(board, p.row, p.col));
          
          if (yugos.length === 3 && empties.length === 1) {
            return {row: empties[0].row, col: empties[0].col};
          }
        }
      }
    }
  }
  
  return null;
};

/**
 * Pattern #3: Opponent Yugo Reveals Threat - Must-block pattern
 * Checks what happens if opponent forms a Yugo - will it reveal a 3-Yugo threat (Igo threat)?
 * If opponent forms a Yugo that removes Migos blocking their threat, we must block that Yugo formation.
 * This is NOT a trapdoor - trapdoor is when YOU form a Yugo that removes YOUR blocking Migo.
 */
export const detectOpponentYugoRevealsThreat = (board: (Cell | null)[][], opponentColor: 'white' | 'black'): {row: number, col: number}[] => {
  const threats: {row: number, col: number}[] = [];
  // Check all 8 directions: horizontal, vertical, and both diagonals
  const directions = [
    [-1, 0],   // up (vertical)
    [0, 1],    // right (horizontal)
    [1, 1],    // down-right (diagonal)
    [1, 0],    // down (vertical)
    [0, -1],   // left (horizontal)
    [-1, 1],   // up-right (diagonal)
    [-1, -1],  // up-left (diagonal)
    [1, -1]    // down-left (diagonal)
  ];
  
  // Check all empty spaces where opponent could form a Yugo
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (!isEmptyCell(board, row, col)) continue;
      
      // Simulate opponent placing a piece here
      const testBoard = copyBoard(board);
      testBoard[row][col] = { color: opponentColor, isYugo: false };
      
      // Check if this forms a Yugo
      const yugos = checkForYugos(testBoard, row, col, opponentColor);
      if (yugos.length === 0) continue; // No Yugo formed, skip
      
      // Process the Yugo: Migos disappear, Yugos remain (use actual game logic)
      const postYugoBoard = copyBoard(testBoard);
      const result = processYugos(postYugoBoard, yugos, row, col);
      if (result.yugoFormed) {
        postYugoBoard[row][col] = { color: opponentColor, isYugo: true };
      }
      
      // Now check if this creates a 3-Yugo threat with exactly 1 empty
      const threeYugoThreats = detectMustBlockThreeYugo(postYugoBoard, opponentColor);
      if (threeYugoThreats.length > 0) {
        // Opponent forming a Yugo here would reveal a 3-Yugo threat - must block this space
        threats.push({row, col});
      }
    }
  }
  
  return threats;
};

/**
 * Pattern #4: Must-avoid - Don't form Yugos that expose threats when Migos disappear
 * Checks if forming a Yugo will remove Migos that were blocking opponent Igo threats (Trapdoor)
 * 
 * Trapdoor pattern: Opponent has 3 pieces (Yugos or Migos) in a row that could form Igo (4 in a row),
 * and there's a blocking Migo of the player's color anywhere in that line (at an end or between).
 * If the player forms a Yugo that includes that blocking Migo, it disappears and reveals the Igo threat.
 */
export const detectYugoTrapExposure = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): boolean => {
  const opponentColor = playerColor === 'white' ? 'black' : 'white';
  
  // First, find all trapdoor patterns in the current board state
  // (opponent has 3 pieces with a blocking Migo of player's color)
  // Only check main directions: horizontal, vertical, and two diagonals
  const directions = [[-1, 0], [0, 1], [1, 1], [1, -1]]; // up, right, down-right, down-left
  const blockingMigPositions = new Set<string>();
  // Also store which direction each blocking Migo is blocking in
  const blockingMigInfo = new Map<string, {direction: number[], segment: {row: number, col: number}[]}>();
  
  // Check all 4-position segments for trapdoor patterns
  for (let startRow = 0; startRow < 8; startRow++) {
    for (let startCol = 0; startCol < 8; startCol++) {
      for (const [dr, dc] of directions) {
        // Build a 4-position segment
        const segment: {row: number, col: number, cell: Cell | null}[] = [];
        for (let i = 0; i < 4; i++) {
          const r = startRow + i * dr;
          const c = startCol + i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            segment.push({row: r, col: c, cell: board[r][c]});
          } else {
            break;
          }
        }
        
        if (segment.length !== 4) continue;
        
        // Check if this segment has trapdoor pattern:
        // - 3 opponent pieces (Yugos or Migos)
        // - 1 blocking Migo of player's color
        // - 0 empty spaces (fully blocked)
        const opponentPieces = segment.filter(p => 
          p.cell?.color === opponentColor
        );
        const blockingMig = segment.find(p => 
          p.cell?.color === playerColor && !p.cell.isYugo
        );
        const empties = segment.filter(p => 
          !p.cell || isEmptyCell(board, p.row, p.col)
        );
        
        // Trapdoor: 3 opponent pieces + 1 blocking Migo + 0 empties
        if (opponentPieces.length === 3 && blockingMig && empties.length === 0) {
          // Store the blocking Migo position and its direction/segment info
          const posKey = `${blockingMig.row},${blockingMig.col}`;
          blockingMigPositions.add(posKey);
          blockingMigInfo.set(posKey, {
            direction: [dr, dc],
            segment: segment.map(p => ({row: p.row, col: p.col}))
          });
          console.log(`[Trapdoor Detection] Found blocking Migo at (${blockingMig.row}, ${blockingMig.col}) blocking ${opponentColor} Igo threat`);
        }
      }
    }
  }
  
  // If no trapdoor patterns found, no trap
  if (blockingMigPositions.size === 0) {
    // Still check for must-block exposure
    const testBoard = copyBoard(board);
    testBoard[row][col] = { color: playerColor, isYugo: false };
    const yugos = checkForYugos(testBoard, row, col, playerColor);
    if (yugos.length === 0) return false;
    
    const postYugoBoard = copyBoard(testBoard);
    const result = processYugos(postYugoBoard, yugos, row, col);
    const removedMigPositions: {row: number, col: number}[] = result.removedCells || [];
    
    if (result.yugoFormed) {
      postYugoBoard[row][col] = { color: playerColor, isYugo: true };
    }
    
    const threeYugoThreats = detectMustBlockThreeYugo(postYugoBoard, opponentColor);
    if (threeYugoThreats.length > 0) {
      // Check if any removed Migo was blocking one of these threats
      if (removedMigPositions.some(pos => 
        threeYugoThreats.some(threat => threat.row === pos.row && threat.col === pos.col)
      )) {
        return true;
      }
    }
    return false;
  }
  
  // Now check if forming a Yugo would remove any of these blocking Migos
  const testBoard = copyBoard(board);
  testBoard[row][col] = { color: playerColor, isYugo: false };
  
  // Check if this forms a Yugo
  const yugos = checkForYugos(testBoard, row, col, playerColor);
  if (yugos.length === 0) return false; // No Yugo formed, no trap
  
  // Process the Yugo: Migos disappear
  const postYugoBoard = copyBoard(testBoard);
  const result = processYugos(postYugoBoard, yugos, row, col);
  const removedMigPositions: {row: number, col: number}[] = result.removedCells || [];
  
  if (result.yugoFormed) {
    postYugoBoard[row][col] = { color: playerColor, isYugo: true };
  }
  
  // Check if any removed Migo was a blocking Migo from a trapdoor pattern
  for (const removedPos of removedMigPositions) {
    const posKey = `${removedPos.row},${removedPos.col}`;
    if (blockingMigPositions.has(posKey)) {
      // This blocking Migo was removed - check if it reveals an Igo threat
      // Use the stored direction and segment info for this blocking Migo
      const migInfo = blockingMigInfo.get(posKey);
      if (migInfo) {
        const [dr, dc] = migInfo.direction;
        // Check the specific segment where this Migo was blocking
        const segment: {row: number, col: number, cell: Cell | null}[] = [];
        for (const segPos of migInfo.segment) {
          segment.push({
            row: segPos.row,
            col: segPos.col,
            cell: postYugoBoard[segPos.row][segPos.col]
          });
        }
        
        // After removing the blocking Migo, check if opponent has 3 pieces + 1 empty
        const opponentPieces = segment.filter(p => 
          p.cell?.color === opponentColor
        );
        const empties = segment.filter(p => 
          !p.cell || isEmptyCell(postYugoBoard, p.row, p.col)
        );
        
        // Trapdoor: After removing blocking Migo, opponent has 3 pieces + 1 empty = Igo threat!
        if (opponentPieces.length === 3 && empties.length === 1) {
          console.log(`[Trapdoor Detection] TRAPDOOR! Move (${row}, ${col}) would remove blocking Migo at (${removedPos.row}, ${removedPos.col}) and reveal ${opponentColor} Igo threat`);
          return true; // Trapdoor! Forming this Yugo reveals the Igo threat
        }
      }
    }
  }
  
  // Also check if removing Migos exposes a 3-Yugo threat (must-block)
  const threeYugoThreats = detectMustBlockThreeYugo(postYugoBoard, opponentColor);
  if (threeYugoThreats.length > 0) {
    // Check if any removed Migo was blocking one of these threats
    if (removedMigPositions.some(pos => 
      threeYugoThreats.some(threat => threat.row === pos.row && threat.col === pos.col)
    )) {
      console.log(`[Trapdoor Detection] TRAPDOOR! Move (${row}, ${col}) would remove blocking Migo and expose ${opponentColor} must-block threat (3 Yugos with 1 empty)`);
      return true;
    }
  }
  
  // Also check if removing Migos exposes a DPA threat (win in 2)
  const dpaThreats = detectDPA(postYugoBoard, opponentColor);
  if (dpaThreats.length > 0) {
    // Check if any removed Migo was blocking one of these DPA threats
    // A DPA threat means opponent can create 3 Yugos with open ends
    // If we removed a Migo that was blocking this, it's a trapdoor
    if (removedMigPositions.some(pos => 
      dpaThreats.some(threat => threat.row === pos.row && threat.col === pos.col)
    )) {
      console.log(`[Trapdoor Detection] TRAPDOOR! Move (${row}, ${col}) would remove blocking Migo and expose ${opponentColor} DPA threat (win in 2)`);
      return true;
    }
  }
  
  return false;
};

/**
 * Detect DPA (Double Play Attack) - positions where opponent can create 3 Yugos with open ends
 * Returns array of blocking positions
 * 
 * Outside DPA: [Migo] [Yugo] [Yugo] [Empty] - 1 block (the empty after Yugos)
 * Inside DPA: [Yugo] [Yugo] [Empty] [Migo] - 2 blocks (empty between, and empty before if allows follow-up)
 */
export const detectDPA = (board: (Cell | null)[][], opponentColor: 'white' | 'black'): {row: number, col: number}[] => {
  const blocks: {row: number, col: number}[] = [];
  const blockSet = new Set<string>(); // Avoid duplicates
  
  // Check all 8 directions
  const directions = [
    [-1, 0],   // up (vertical)
    [0, 1],    // right (horizontal)
    [1, 1],    // down-right (diagonal)
    [1, 0],    // down (vertical)
    [0, -1],   // left (horizontal)
    [-1, 1],   // up-right (diagonal)
    [-1, -1],  // up-left (diagonal)
    [1, -1]    // down-left (diagonal)
  ];
  
  // Check all possible lines in all directions
  for (let startRow = 0; startRow < 8; startRow++) {
    for (let startCol = 0; startCol < 8; startCol++) {
      for (const [dr, dc] of directions) {
        // Build the full line starting from this position (need at least 5 positions for DPA)
        const line: {row: number, col: number, cell: Cell | null}[] = [];
        for (let i = 0; i < 8; i++) {
          const r = startRow + i * dr;
          const c = startCol + i * dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8) {
            line.push({row: r, col: c, cell: board[r][c]});
          } else {
            break;
          }
        }
        
        // Need at least 4 positions to check for DPA patterns
        if (line.length < 4) continue;
        
        // Check for OUTSIDE DPA: [Migo] [Yugo] [Yugo] [Empty]
        // Pattern: opponent Migo, then 2 opponent Yugos, then empty
        for (let i = 0; i <= line.length - 4; i++) {
          const pos1 = line[i];     // Migo position
          const pos2 = line[i + 1]; // Yugo 1
          const pos3 = line[i + 2]; // Yugo 2
          const pos4 = line[i + 3]; // Empty (key block position)
          
          // Check if pos1 is opponent Migo (not Yugo, not empty, not our color)
          if (!pos1.cell || pos1.cell.isYugo || pos1.cell.color !== opponentColor) continue;
          
          // Check if pos2 and pos3 are opponent Yugos
          if (!pos2.cell || !pos2.cell.isYugo || pos2.cell.color !== opponentColor) continue;
          if (!pos3.cell || !pos3.cell.isYugo || pos3.cell.color !== opponentColor) continue;
          
          // Check if pos4 is empty
          if (pos4.cell && !isEmptyCell(board, pos4.row, pos4.col)) continue;
          
          // Simulate opponent playing on pos4
          const testBoard = copyBoard(board);
          testBoard[pos4.row][pos4.col] = { color: opponentColor, isYugo: false };
          
          // Check if this forms a Yugo
          const yugos = checkForYugos(testBoard, pos4.row, pos4.col, opponentColor);
          if (yugos.length === 0) continue; // No Yugo formed, not a DPA
          
          // Process the Yugo: Migos disappear
          const postYugoBoard = copyBoard(testBoard);
          const result = processYugos(postYugoBoard, yugos, pos4.row, pos4.col);
          if (result.yugoFormed) {
            postYugoBoard[pos4.row][pos4.col] = { color: opponentColor, isYugo: true };
          }
          
          // Check if this creates 3 Yugos with open ends (DPA threat)
          // Look for 3 consecutive Yugos with empty on both sides
          const checkLine: {row: number, col: number, cell: Cell | null}[] = [];
          for (let j = 0; j < 8; j++) {
            const r = startRow + j * dr;
            const c = startCol + j * dc;
            if (r >= 0 && r < 8 && c >= 0 && c < 8) {
              checkLine.push({row: r, col: c, cell: postYugoBoard[r][c]});
            } else {
              break;
            }
          }
          
          // Check for 3 Yugos with empty on both ends
          for (let j = 0; j <= checkLine.length - 5; j++) {
            const before = checkLine[j];
            const yugo1 = checkLine[j + 1];
            const yugo2 = checkLine[j + 2];
            const yugo3 = checkLine[j + 3];
            const after = checkLine[j + 4];
            
            // Check if before and after are empty
            if (before.cell && !isEmptyCell(postYugoBoard, before.row, before.col)) continue;
            if (after.cell && !isEmptyCell(postYugoBoard, after.row, after.col)) continue;
            
            // Check if middle 3 are opponent Yugos
            if (!yugo1.cell || !yugo1.cell.isYugo || yugo1.cell.color !== opponentColor) continue;
            if (!yugo2.cell || !yugo2.cell.isYugo || yugo2.cell.color !== opponentColor) continue;
            if (!yugo3.cell || !yugo3.cell.isYugo || yugo3.cell.color !== opponentColor) continue;
            
            // Found Outside DPA! Block on pos4
            const posKey = `${pos4.row},${pos4.col}`;
            if (!blockSet.has(posKey)) {
              blocks.push({row: pos4.row, col: pos4.col});
              blockSet.add(posKey);
            }
            break; // Found DPA in this segment, move on
          }
        }
        
        // Check for INSIDE DPA: [Yugo] [Yugo] [Empty] [Migo]
        // Pattern: 2 opponent Yugos, then empty (key block), then opponent Migo
        for (let i = 0; i <= line.length - 4; i++) {
          const pos1 = line[i];     // Yugo 1
          const pos2 = line[i + 1]; // Yugo 2
          const pos3 = line[i + 2]; // Empty (key block position)
          const pos4 = line[i + 3]; // Migo
          
          // Check if pos1 and pos2 are opponent Yugos
          if (!pos1.cell || !pos1.cell.isYugo || pos1.cell.color !== opponentColor) continue;
          if (!pos2.cell || !pos2.cell.isYugo || pos2.cell.color !== opponentColor) continue;
          
          // Check if pos3 is empty
          if (pos3.cell && !isEmptyCell(board, pos3.row, pos3.col)) continue;
          
          // Check if pos4 is opponent Migo (not Yugo, not empty, not our color)
          if (!pos4.cell || pos4.cell.isYugo || pos4.cell.color !== opponentColor) continue;
          
          // Simulate opponent playing on pos3
          const testBoard = copyBoard(board);
          testBoard[pos3.row][pos3.col] = { color: opponentColor, isYugo: false };
          
          // Check if this forms a Yugo
          const yugos = checkForYugos(testBoard, pos3.row, pos3.col, opponentColor);
          if (yugos.length === 0) continue; // No Yugo formed, not a DPA
          
          // Process the Yugo: Migos disappear
          const postYugoBoard = copyBoard(testBoard);
          const result = processYugos(postYugoBoard, yugos, pos3.row, pos3.col);
          if (result.yugoFormed) {
            postYugoBoard[pos3.row][pos3.col] = { color: opponentColor, isYugo: true };
          }
          
          // Check if this creates 3 Yugos with open ends (DPA threat)
          const checkLine: {row: number, col: number, cell: Cell | null}[] = [];
          for (let j = 0; j < 8; j++) {
            const r = startRow + j * dr;
            const c = startCol + j * dc;
            if (r >= 0 && r < 8 && c >= 0 && c < 8) {
              checkLine.push({row: r, col: c, cell: postYugoBoard[r][c]});
            } else {
              break;
            }
          }
          
          // Check for 3 Yugos with empty on both ends
          for (let j = 0; j <= checkLine.length - 5; j++) {
            const before = checkLine[j];
            const yugo1 = checkLine[j + 1];
            const yugo2 = checkLine[j + 2];
            const yugo3 = checkLine[j + 3];
            const after = checkLine[j + 4];
            
            // Check if before and after are empty
            if (before.cell && !isEmptyCell(postYugoBoard, before.row, before.col)) continue;
            if (after.cell && !isEmptyCell(postYugoBoard, after.row, after.col)) continue;
            
            // Check if middle 3 are opponent Yugos
            if (!yugo1.cell || !yugo1.cell.isYugo || yugo1.cell.color !== opponentColor) continue;
            if (!yugo2.cell || !yugo2.cell.isYugo || yugo2.cell.color !== opponentColor) continue;
            if (!yugo3.cell || !yugo3.cell.isYugo || yugo3.cell.color !== opponentColor) continue;
            
            // Found Inside DPA! Block on pos3 (empty between Yugos and Migo)
            const posKey1 = `${pos3.row},${pos3.col}`;
            if (!blockSet.has(posKey1)) {
              blocks.push({row: pos3.row, col: pos3.col});
              blockSet.add(posKey1);
            }
            
            // Also check if there's an empty before pos1 that allows follow-up block
            if (i > 0) {
              const beforePos = line[i - 1];
              if (!beforePos.cell || isEmptyCell(board, beforePos.row, beforePos.col)) {
                // Check if blocking here allows follow-up block after opponent plays
                // Simulate opponent playing on pos3 after we block before
                const testBoard2 = copyBoard(board);
                testBoard2[beforePos.row][beforePos.col] = { color: opponentColor === 'white' ? 'black' : 'white', isYugo: false }; // Our blocking piece
                testBoard2[pos3.row][pos3.col] = { color: opponentColor, isYugo: false }; // Opponent plays
                
                const yugos2 = checkForYugos(testBoard2, pos3.row, pos3.col, opponentColor);
                if (yugos2.length > 0) {
                  const postYugoBoard2 = copyBoard(testBoard2);
                  const result2 = processYugos(postYugoBoard2, yugos2, pos3.row, pos3.col);
                  if (result2.yugoFormed) {
                    postYugoBoard2[pos3.row][pos3.col] = { color: opponentColor, isYugo: true };
                  }
                  
                  // Check if we can still block after opponent plays
                  // Look for the position we need to block (the empty after the 3 Yugos)
                  const checkLine2: {row: number, col: number, cell: Cell | null}[] = [];
                  for (let j = 0; j < 8; j++) {
                    const r = startRow + j * dr;
                    const c = startCol + j * dc;
                    if (r >= 0 && r < 8 && c >= 0 && c < 8) {
                      checkLine2.push({row: r, col: c, cell: postYugoBoard2[r][c]});
                    } else {
                      break;
                    }
                  }
                  
                  // Find the 3 Yugos and check if there's a blockable position after
                  // After blocking before, one end is blocked, but we need to be able to block the other end
                  for (let j = 0; j <= checkLine2.length - 5; j++) {
                    const before2 = checkLine2[j];
                    const yugo1_2 = checkLine2[j + 1];
                    const yugo2_2 = checkLine2[j + 2];
                    const yugo3_2 = checkLine2[j + 3];
                    const after2 = checkLine2[j + 4];
                    
                    // Check if middle 3 are opponent Yugos
                    if (!yugo1_2.cell || !yugo1_2.cell.isYugo || yugo1_2.cell.color !== opponentColor) continue;
                    if (!yugo2_2.cell || !yugo2_2.cell.isYugo || yugo2_2.cell.color !== opponentColor) continue;
                    if (!yugo3_2.cell || !yugo3_2.cell.isYugo || yugo3_2.cell.color !== opponentColor) continue;
                    
                    // Before might be our blocking piece (that's fine), but after must be empty and blockable
                    if (after2.cell && !isEmptyCell(postYugoBoard2, after2.row, after2.col)) continue;
                    
                    // Found the follow-up block position (after2 is empty and blockable)
                    // This means blocking on beforePos is valid - we can block on after2 next turn
                    const posKey2 = `${beforePos.row},${beforePos.col}`;
                    if (!blockSet.has(posKey2)) {
                      blocks.push({row: beforePos.row, col: beforePos.col});
                      blockSet.add(posKey2);
                    }
                    break;
                  }
                }
              }
            }
            
            break; // Found DPA in this segment, move on
          }
        }
      }
    }
  }
  
  return blocks;
};

// ============================================================================
// CONNECTED PIECES AND OPENING POSITION EVALUATION FOR AI-3
// ============================================================================

/**
 * Count total pieces on the board (to determine game phase)
 */
const countTotalPieces = (board: (Cell | null)[][]): number => {
  let count = 0;
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (board[row][col] !== null) {
        count++;
      }
    }
  }
  return count;
};

/**
 * Check if we're in the opening phase (first 5-15 moves)
 */
const isOpeningPhase = (board: (Cell | null)[][]): boolean => {
  const totalPieces = countTotalPieces(board);
  return totalPieces >= 0 && totalPieces < 30; // 0-29 pieces = opening (15 moves per player max)
};

/**
 * Detect connected pieces - 2+ pieces on same line
 * CRITICAL: Pieces separated by opponent Migos ARE connected (Migos are ephemeral)
 * CRITICAL: Pieces separated by opponent Yugos are NOT connected (Yugos are permanent)
 * Returns the total number of pieces in connected groups (value increases with group size)
 */
const countConnectedPieces = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): number => {
  const directions = [[-1, 0], [0, 1], [1, 1], [1, 0]]; // Horizontal, Vertical, Diagonals
  let totalConnectedValue = 0;
  const opponentColor = playerColor === 'white' ? 'black' : 'white';
  
  // Simulate placing the piece
  const testBoard = copyBoard(board);
  testBoard[row][col] = { color: playerColor, isYugo: false };
  
  for (const [dr, dc] of directions) {
    const line: {row: number, col: number, cell: Cell | null, index: number}[] = [];
    
    // Build the line in both directions (need longer line to check connections)
    for (let i = -4; i <= 4; i++) {
      const r = row + i * dr;
      const c = col + i * dc;
      if (r >= 0 && r < 8 && c >= 0 && c < 8) {
        line.push({row: r, col: c, cell: testBoard[r][c], index: i + 4}); // index 0-8
      }
    }
    
    // Check for connected pieces in this line
    // CRITICAL: We need to check if pieces are connected, accounting for opponent Migos vs Yugos
    let playerPieces: {row: number, col: number, index: number}[] = [];
    
    // First, find all player pieces and check if any opponent Yugo blocks the line
    let hasOpponentYugoBetween = false;
    let firstPlayerPieceIndex = -1;
    let lastPlayerPieceIndex = -1;
    
    for (let i = 0; i < line.length; i++) {
      const pos = line[i];
      if (pos.cell) {
        if (pos.cell.color === playerColor) {
          playerPieces.push({row: pos.row, col: pos.col, index: pos.index});
          if (firstPlayerPieceIndex === -1) firstPlayerPieceIndex = i;
          lastPlayerPieceIndex = i;
        } else if (pos.cell.color === opponentColor && pos.cell.isYugo) {
          // Opponent Yugo found - check if it's between player pieces
          if (firstPlayerPieceIndex !== -1 && i > firstPlayerPieceIndex && i < lastPlayerPieceIndex) {
            hasOpponentYugoBetween = true;
            break; // Opponent Yugo blocks connection between pieces
          }
        }
        // Opponent Migos are ignored - they don't block connections
      }
    }
    
    // If no opposing Yugo blocks, find connected groups
    // Pieces are connected if there's no opponent Yugo between them
    // Opponent Migos don't break connections (they're ephemeral)
    if (!hasOpponentYugoBetween && playerPieces.length >= 2) {
      // Sort pieces by position in the line
      playerPieces.sort((a, b) => a.index - b.index);
      
      // Find connected groups
      // Two pieces are connected if:
      // 1. No opponent Yugo between them
      // 2. They're on the same line (already checked)
      // 3. Distance between them (accounting for opponent Migos) is reasonable
      let currentGroup: typeof playerPieces = [playerPieces[0]];
      
      for (let i = 1; i < playerPieces.length; i++) {
        // Check if there's an opponent Yugo between these two pieces
        let yugoBetween = false;
        const startIdx = playerPieces[i - 1].index;
        const endIdx = playerPieces[i].index;
        
        for (let j = startIdx + 1; j < endIdx; j++) {
          const linePos = line.find(p => p.index === j);
          if (linePos && linePos.cell && 
              linePos.cell.color === opponentColor && linePos.cell.isYugo) {
            yugoBetween = true;
            break;
          }
        }
        
        // If no Yugo between, pieces are connected (opponent Migos don't matter)
        if (!yugoBetween) {
          currentGroup.push(playerPieces[i]); // Connected to previous piece
        } else {
          // End of current group, add its value
          if (currentGroup.length >= 2) {
            totalConnectedValue += currentGroup.length; // Value increases with group size
          }
          currentGroup = [playerPieces[i]]; // Start new group
        }
      }
      
      // Don't forget the last group
      if (currentGroup.length >= 2) {
        totalConnectedValue += currentGroup.length;
      }
    }
  }
  
  return totalConnectedValue;
};

/**
 * Evaluate opening position based on ring system
 * Returns position value: outer ring (worst) < middle < center < ring around center (best)
 */
const evaluateOpeningPosition = (row: number, col: number): number => {
  // Calculate distance from center
  const centerRow = 3.5;
  const centerCol = 3.5;
  const distanceFromCenter = Math.sqrt(Math.pow(row - centerRow, 2) + Math.pow(col - centerCol, 2));
  
  // Define rings with stronger values to override center control:
  // Outer ring: distance > 3.5 (edges/corners) - WORST (strong penalty)
  // Middle ring: 2.5 < distance <= 3.5 - BAD (small penalty)
  // Center ring: 1.5 < distance <= 2.5 - NEUTRAL (avoid center in opening)
  // Ring around center: distance <= 1.5 - BEST (strong bonus)
  
  if (distanceFromCenter > 3.5) {
    return -50; // Outer ring - strong penalty (avoid edges)
  } else if (distanceFromCenter > 2.5) {
    return -10; // Middle ring - small penalty
  } else if (distanceFromCenter > 1.5) {
    return -5; // Center ring - slight penalty (avoid center in opening, Migos will disappear)
  } else {
    return 40; // Ring around center - strong bonus (best position)
  }
};

/**
 * Detect if placing a piece would block future opportunities when opponent Migos disappear
 * Pattern: W, W, B, W, W - if B disappears, can't place on B's spot (would create 5 in a row)
 * Returns true if this move blocks a future opportunity
 */
const detectBlocksFutureOpportunity = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): boolean => {
  const opponentColor = playerColor === 'white' ? 'black' : 'white';
  const directions = [[-1, 0], [0, 1], [1, 1], [1, 0]]; // Horizontal, Vertical, Diagonals
  
  // Simulate placing the piece
  const testBoard = copyBoard(board);
  testBoard[row][col] = { color: playerColor, isYugo: false };
  
  for (const [dr, dc] of directions) {
    // Check the full line in both directions from the placed piece
    let piecesBeforeOpponent = 0;
    let piecesAfterOpponent = 0;
    let opponentMigoPosition: {row: number, col: number} | null = null;
    let foundOpponentMigo = false;
    
    // Count pieces in negative direction (before the placed piece)
    let r = row - dr;
    let c = col - dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8) {
      const cell = testBoard[r][c];
      if (!cell) break; // Empty cell stops the line
      
      if (cell.color === playerColor) {
        piecesBeforeOpponent++;
        r -= dr;
        c -= dc;
      } else if (cell.color === opponentColor && !cell.isYugo) {
        // Found opponent Migo - this is the key position
        foundOpponentMigo = true;
        opponentMigoPosition = {row: r, col: c};
        break;
      } else {
        // Opponent Yugo or other color - stops the pattern
        break;
      }
    }
    
    // If we found an opponent Migo, count pieces after it (in positive direction from placed piece)
    if (foundOpponentMigo && opponentMigoPosition) {
      // Count pieces after the opponent Migo (continuing in the same direction)
      r = opponentMigoPosition.row + dr;
      c = opponentMigoPosition.col + dc;
      while (r >= 0 && r < 8 && c >= 0 && c < 8) {
        const cell = testBoard[r][c];
        if (!cell) break; // Empty cell stops the line
        
        if (cell.color === playerColor) {
          piecesAfterOpponent++;
          r += dr;
          c += dc;
        } else {
          break; // Different color stops the pattern
        }
      }
      
      // Pattern detected: We have pieces on both sides of opponent Migo, plus the piece we just placed
      // Total pieces on our side = piecesBeforeOpponent + 1 (placed piece) + piecesAfterOpponent
      // If total >= 4, then when opponent Migo disappears, placing on that spot would create 5+ in a row
      const totalPieces = piecesBeforeOpponent + 1 + piecesAfterOpponent;
      if (totalPieces >= 4) {
        // Simulate removing the opponent Migo and check if placing on that spot would be illegal
        const simulatedBoard = copyBoard(testBoard);
        simulatedBoard[opponentMigoPosition.row][opponentMigoPosition.col] = null; // Remove opponent Migo
        
        // Check if placing on that spot would create 5+ in a row (illegal)
        if (wouldCreateLineTooLong(simulatedBoard, opponentMigoPosition.row, opponentMigoPosition.col, playerColor)) {
          return true; // This move blocks a future opportunity
        }
      }
    }
  }
  
  return false;
};

// Move evaluation function
/**
 * Check if two pieces are connected (on same line with ≤2 squares between, no opponent Yugos between)
 * Pieces are connected if:
 * - They're touching (no spaces between), OR
 * - They have ≤2 empty squares between them, OR
 * - They have opponent Migos between them (Migos can disappear)
 * - They do NOT have opponent Yugos between them (Yugos are permanent)
 */
const _arePiecesConnected = (board: (Cell | null)[][], pos1: {row: number, col: number}, pos2: {row: number, col: number}, playerColor: 'white' | 'black'): boolean => {
  const opponentColor = playerColor === 'white' ? 'black' : 'white';
  
  // Check all 8 directions
  const directions = [
    [-1, 0], [0, 1], [1, 1], [1, 0], [0, -1], [-1, 1], [-1, -1], [1, -1]
  ];
  
  for (const [dr, dc] of directions) {
    // Check if pos1 and pos2 are on the same line in this direction
    const dx = pos2.col - pos1.col;
    const dy = pos2.row - pos1.row;
    
    // Check if they're aligned in this direction
    if (dx === 0 && dy === 0) continue; // Same position
    
    // Check if direction matches
    if (dx === 0 && dy === 0) continue;
    if (dx === 0 && dy !== 0) {
      // Vertical line
      if (dr === 0) continue; // Not vertical direction
      if (Math.sign(dy) !== Math.sign(dr)) continue; // Wrong direction
    } else if (dx !== 0 && dy === 0) {
      // Horizontal line
      if (dc === 0) continue; // Not horizontal direction
      if (Math.sign(dx) !== Math.sign(dc)) continue; // Wrong direction
    } else if (dx !== 0 && dy !== 0) {
      // Diagonal line
      if (dr === 0 || dc === 0) continue; // Not diagonal direction
      if (Math.abs(dx) !== Math.abs(dy)) continue; // Not 45-degree diagonal
      if (Math.sign(dx) !== Math.sign(dc) || Math.sign(dy) !== Math.sign(dr)) continue; // Wrong direction
    } else {
      continue; // Invalid
    }
    
    // They're on the same line - check what's between them
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    let emptyCount = 0;
    let hasOpponentYugo = false;
    
    for (let i = 1; i < steps; i++) {
      const r = pos1.row + i * dr;
      const c = pos1.col + i * dc;
      const cell = board[r][c];
      
      if (!cell || isEmptyCell(board, r, c)) {
        emptyCount++;
      } else if (cell.color === opponentColor && cell.isYugo) {
        hasOpponentYugo = true;
        break; // Opponent Yugo blocks connection
      }
      // Opponent Migo is OK (can disappear)
    }
    
    // Connected if ≤2 empties and no opponent Yugo between
    if (!hasOpponentYugo && emptyCount <= 2) {
      return true;
    }
  }
  
  return false;
};

export const evaluateMove = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black', difficulty: 'ai-1' | 'ai-2' | 'ai-3' = 'ai-3'): number => {
  
  let score = 0;
  const opponentColor = playerColor === 'white' ? 'black' : 'white';
  
  // ABSOLUTE PRIORITY 0: Immediate Igo Win (check if this move wins the game)
  // Create a fresh copy for this check to prevent state corruption
  const igoTestBoard = copyBoard(board);
  igoTestBoard[row][col] = { color: playerColor, isYugo: true };
  const immediateIgo = checkForIgo(igoTestBoard, row, col, playerColor);
  if (immediateIgo) {
    score += 100000; // MASSIVE BONUS - ALWAYS TAKE WINNING MOVES!
  }
  
  // AI-3: MUST-MOVE PATTERNS (highest priority after immediate win)
  if (difficulty === 'ai-3') {
    // Pattern #2: Must-win - 3 Yugos with exactly 1 empty space
    const mustWin = detectMustWinThreeYugo(board, playerColor);
    if (mustWin && mustWin.row === row && mustWin.col === col) {
      score += 50000; // MUST WIN - highest priority (except immediate Igo)
      return score; // Return immediately, nothing else matters
    }
    
    // Pattern #1: Must-block - Opponent has 3 Yugos with exactly 1 empty space
    const mustBlockThreats = detectMustBlockThreeYugo(board, opponentColor);
    if (mustBlockThreats.some(threat => threat.row === row && threat.col === col)) {
      score += 40000; // MUST BLOCK - second highest priority
      return score; // Return immediately, blocking is critical
    }
    
    // Pattern #3: Opponent Yugo Reveals Threat - Must-block to prevent opponent from forming Yugo that reveals threat
    const opponentYugoThreats = detectOpponentYugoRevealsThreat(board, opponentColor);
    if (opponentYugoThreats.some(threat => threat.row === row && threat.col === col)) {
      score += 35000; // MUST BLOCK - prevent opponent from forming Yugo that reveals threat
      return score; // Return immediately, prevent the threat reveal
    }
    
    // Pattern #4: Must-avoid - Don't form Yugos that expose threats
    if (detectYugoTrapExposure(board, row, col, playerColor)) {
      score -= 50000; // MUST AVOID - heavy penalty, but don't return (might be forced)
    }
  }
  
  // PRIORITY 1: Yugo Formation (immediate win condition)
  // Create a fresh copy for this check to prevent state corruption
  const yugoTestBoard = copyBoard(board);
  yugoTestBoard[row][col] = { color: playerColor, isYugo: false };
  
  const yugos = checkForYugos(yugoTestBoard, row, col, playerColor);
  if (yugos.length > 0) {
    score += 1000 * yugos.length; // Massive bonus for forming yugos
  }
  
  // AI-3: Enhanced evaluation with improved scoring and simple lookahead
  if (difficulty === 'ai-3') {
    // Improved scoring multipliers for AI-3
    // (Base evaluation continues below with enhanced values)
  }
  
  // PRIORITY 2: Connected Pieces (building blocks for future Yugos/Igos) - HIGHER PRIORITY THAN BLOCKING
  if (difficulty === 'ai-3') {
    const connections = countConnectedPieces(board, row, col, playerColor);
    // Very high value for connected pieces - building is more important than blocking
    // Larger groups are exponentially more valuable (2 pieces = 100, 3 pieces = 300, 4 pieces = 600)
    if (connections >= 4) {
      score += 600; // 4+ connected pieces - very strong formation
    } else if (connections >= 3) {
      score += 300; // 3 connected pieces - strong formation
    } else if (connections >= 2) {
      score += 100; // 2 connected pieces - building formation
    }
    // Also add base value for any connections
    score += connections * 30; // Additional value per connected piece
    
    // Check if this move blocks future opportunities when opponent Migos disappear
    // Pattern: W, W, B, W, W - if B disappears, can't place on B's spot (would create 5 in a row)
    const blocksFutureOpportunity = detectBlocksFutureOpportunity(board, row, col, playerColor);
    if (blocksFutureOpportunity) {
      score -= 800; // Heavy penalty for blocking own future opportunities
    }
  }
  
  // PRIORITY 3: Block Opponent Threats (prevent opponent from winning)
  // IMPORTANT: Blocking with Yugo is much more powerful than blocking with Migo
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (isValidMove(board, r, c, opponentColor)) {
        const opponentTestBoard = copyBoard(board);
        opponentTestBoard[r][c] = { color: opponentColor, isYugo: false };
        const opponentYugos = checkForYugos(opponentTestBoard, r, c, opponentColor);
        
        // Check for igo threat (opponent can win immediately)
        opponentTestBoard[r][c] = { color: opponentColor, isYugo: true };
        const opponentIgo = checkForIgo(opponentTestBoard, r, c, opponentColor);
        
        if ((opponentYugos.length > 0 || opponentIgo) && r === row && c === col) {
          if (opponentIgo) {
            // Blocking Igo is critical - always high value
            if (difficulty === 'ai-3' && yugos.length > 0) {
              score += 12000; // Blocking with Yugo (permanent) - very powerful
            } else {
              score += 9000; // Blocking with Migo (temporary, risky)
            }
          } else {
            // Blocking opponent Yugos
            if (difficulty === 'ai-3') {
              if (yugos.length > 0) {
                // Blocking with Yugo (permanent) - much more powerful
                score += 1500 + (opponentYugos.length * 300); // Strong permanent block
              } else {
                // Blocking with Migo (temporary, risky) - lower value
                score += 400 + (opponentYugos.length * 100); // Weaker temporary block
              }
            } else {
              score += 800; // Original blocking bonus for AI-1, AI-2
            }
          }
        }
      }
    }
  }
  
  // PRIORITY 4: Check for Igo Formation (game winner)
  // Create a fresh copy for this check to prevent state corruption
  const igoTestBoard2 = copyBoard(board);
  igoTestBoard2[row][col] = { color: playerColor, isYugo: true };
  const igo = checkForIgo(igoTestBoard2, row, col, playerColor);
  if (igo) {
    score += 10000; // Instant win
  }
  
  // PRIORITY 5: Yugo Building (scoring opportunities)
  if (yugos.length > 0) {
    if (difficulty === 'ai-3') {
      score += 150;
    } else {
      score += 100;
    }
  }
  
  // PRIORITY 6: Position Evaluation
  if (difficulty === 'ai-3') {
    // AI-3: Strategic position evaluation
    const inOpening = isOpeningPhase(board);
    const formsYugo = yugos.length > 0;
    
    if (inOpening) {
      // Opening: Use ring-based evaluation ONLY (no center control bonus)
      // Ring around center is best, outer ring is worst
      score += evaluateOpeningPosition(row, col);
      // Do NOT add any center control bonus in opening - ring evaluation is enough
    } else {
      // Mid/late game: Center is valuable, but only for Yugos (permanent control)
      if (formsYugo) {
        // Value center for Yugos (permanent)
        const centerDistance = Math.abs(row - 3.5) + Math.abs(col - 3.5);
        score += (7 - centerDistance) * 15;
      } else {
        // Migos are temporary - don't value center as much
        const centerDistance = Math.abs(row - 3.5) + Math.abs(col - 3.5);
        score += (7 - centerDistance) * 3; // Much lower value for temporary Migos
      }
    }
  } else {
    // AI-1, AI-2: Original center control bonus
    const centerDistance = Math.abs(row - 3.5) + Math.abs(col - 3.5);
    score += (7 - centerDistance) * 10;
  }
  
  // PRIORITY 7: Support Structures (set up future yugos)
  let supportCount = 0;
  const directions = [[-1, 0], [-1, 1], [0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1]];
  for (const [dr, dc] of directions) {
    const adjRow = row + dr;
    const adjCol = col + dc;
    if (adjRow >= 0 && adjRow < 8 && adjCol >= 0 && adjCol < 8) {
      if (board[adjRow][adjCol] && board[adjRow][adjCol]!.color === playerColor) {
        supportCount++;
      }
    }
  }
  // AI-3: Better support structure bonus
  if (difficulty === 'ai-3') {
    score += supportCount * 30; // Increased from 20 to 30
  } else {
    score += supportCount * 20; // Original bonus
  }
  
  // Level 1 specific: Add some small evaluation noise to make it less perfect
  if (difficulty === 'ai-1') {
    score += Math.random() * 40 - 20; // ±20 point variation
  }
  
  // AI-3: Simple one-move lookahead - check opponent's best response
  if (difficulty === 'ai-3') {
    const testBoard = makeMove(board, row, col, playerColor);
    const opponentMoves: { row: number; col: number; score: number }[] = [];
    
    // Find all valid moves for opponent after this move
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if (isValidMove(testBoard, r, c, opponentColor)) {
          // Use AI-2 evaluation for opponent (simpler, faster)
          const oppScore = evaluateMove(testBoard, r, c, opponentColor, 'ai-2');
          opponentMoves.push({ row: r, col: c, score: oppScore });
        }
      }
    }
    
    if (opponentMoves.length > 0) {
      opponentMoves.sort((a, b) => b.score - a.score);
      const opponentBestScore = opponentMoves[0].score;
      
      // Penalize moves that give opponent very strong responses (but not winning moves)
      // If opponent can get a score >= 1000 (yugo formation), reduce our score
      if (opponentBestScore >= 1000 && opponentBestScore < 9000) {
        // Penalty scales with how good opponent's move is
        score -= Math.min(500, (opponentBestScore - 1000) / 10);
      }
      
      // Heavy penalty if opponent can block our yugos or create their own
      if (opponentBestScore >= 800 && opponentBestScore < 1000) {
        score -= 200;
      }
    }
  }
  
  return score;
};

export {
  evaluatePosition,
  evaluateThreats,
  evaluatePositional,
  evaluateMobility,
  evaluateStrategic,
  evaluateTactical
};

